/**
 * useDevice - Composable for responsive device detection
 *
 * Provides reactive breakpoints and device capabilities detection
 * for mobile/tablet/desktop layouts.
 *
 * Breakpoints (Tailwind-aligned):
 * - Mobile: < 768px (sm)
 * - Tablet: 768px - 1024px (md)
 * - Desktop: >= 1024px (lg+)
 */

import { ref, computed, onMounted, onUnmounted } from 'vue'

// Breakpoints aligned with Tailwind defaults
const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
} as const

export function useDevice() {
  const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024)
  const windowHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 768)

  // Reactive breakpoints (CSS-based)
  const isMobile = computed(() => windowWidth.value < BREAKPOINTS.md)
  const isTablet = computed(() => windowWidth.value >= BREAKPOINTS.md && windowWidth.value < BREAKPOINTS.lg)
  const isDesktop = computed(() => windowWidth.value >= BREAKPOINTS.lg)
  const isLargeDesktop = computed(() => windowWidth.value >= BREAKPOINTS.xl)

  // Physical tablet detection — touch device with screen up to 1400px
  // Covers landscape tablets (Samsung Tab S7: 1280px, iPad Pro 12.9": 1366px)
  const isTabletDevice = computed(() => {
    if (typeof window === 'undefined') return false
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    const isReasonableSize = windowWidth.value < 1400
    const isNotPhone = windowWidth.value >= BREAKPOINTS.md
    return hasTouch && isReasonableSize && isNotPhone
  })

  // Orientation
  const isPortrait = computed(() => windowHeight.value > windowWidth.value)
  const isLandscape = computed(() => windowWidth.value >= windowHeight.value)

  // Touch capability detection
  const isTouchDevice = computed(() => {
    if (typeof window === 'undefined') return false
    return 'ontouchstart' in window ||
           navigator.maxTouchPoints > 0 ||
           // @ts-ignore - msMaxTouchPoints for older IE/Edge
           navigator.msMaxTouchPoints > 0
  })

  // iOS detection (useful for safe-area handling)
  const isIOS = computed(() => {
    if (typeof navigator === 'undefined') return false
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  })

  // Android detection
  const isAndroid = computed(() => {
    if (typeof navigator === 'undefined') return false
    return /Android/.test(navigator.userAgent)
  })

  // PWA standalone mode detection
  const isStandalone = computed(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(display-mode: standalone)').matches ||
           // @ts-ignore - iOS Safari standalone
           (window.navigator as any).standalone === true
  })

  // Current breakpoint name
  const breakpoint = computed(() => {
    if (windowWidth.value < BREAKPOINTS.sm) return 'xs'
    if (windowWidth.value < BREAKPOINTS.md) return 'sm'
    if (windowWidth.value < BREAKPOINTS.lg) return 'md'
    if (windowWidth.value < BREAKPOINTS.xl) return 'lg'
    if (windowWidth.value < BREAKPOINTS['2xl']) return 'xl'
    return '2xl'
  })

  // Resize handler with debounce
  let resizeTimeout: ReturnType<typeof setTimeout> | null = null

  function handleResize() {
    if (resizeTimeout) {
      clearTimeout(resizeTimeout)
    }
    resizeTimeout = setTimeout(() => {
      windowWidth.value = window.innerWidth
      windowHeight.value = window.innerHeight
    }, 100) // 100ms debounce
  }

  onMounted(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize, { passive: true })
      // Initial values
      windowWidth.value = window.innerWidth
      windowHeight.value = window.innerHeight
    }
  })

  onUnmounted(() => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', handleResize)
    }
    if (resizeTimeout) {
      clearTimeout(resizeTimeout)
    }
  })

  return {
    // Dimensions
    windowWidth,
    windowHeight,

    // Breakpoints
    isMobile,
    isTablet,
    isTabletDevice,
    isDesktop,
    isLargeDesktop,
    breakpoint,

    // Orientation
    isPortrait,
    isLandscape,

    // Capabilities
    isTouchDevice,
    isIOS,
    isAndroid,
    isStandalone,

    // Constants
    BREAKPOINTS
  }
}

export default useDevice
